import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Sparkles, Loader2, Mic, Zap, Smartphone, Instagram, Youtube } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useEntitlement, creditHint } from "@/hooks/useVideo";
import { PhoneDemo } from "@/components/site/PhoneDemo";
import { CreationProgress } from "@/components/video/CreationProgress";
import { VideoResult } from "@/components/video/VideoResult";
import { PaywallDialog } from "@/components/video/PaywallDialog";
import { MusicPicker } from "@/components/video/MusicPicker";
import { BrandPhotos } from "@/components/video/BrandPhotos";

const DRAFT = "mimis_video_draft";
const JOB = "mimis_video_job";
const VOICES = [
  { id: "giulia", label: "Giulia", desc: "Voce calda amichevole", icon: Mic },
  { id: "marco", label: "Marco", desc: "Voce energica", icon: Zap },
];
const FORMATS = [
  { id: "tiktok", label: "TikTok 9:16", icon: Smartphone },
  { id: "reel", label: "Instagram Reel", icon: Instagram },
  { id: "short", label: "YouTube Short", icon: Youtube },
];

const Choice = ({ active, onClick, children, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId}
    className={`flex items-center gap-3 rounded-2xl border p-4 text-left transition-[border-color,background-color,transform] duration-200 hover:-translate-y-0.5 ${active ? "border-[#0066FF] bg-[#0066FF]/[0.06] ring-1 ring-[#0066FF]" : "border-[#e7e7ea] bg-white hover:border-[#c7c7cc]"}`}>
    {children}
  </button>
);

const readDraft = () => {
  try {
    return JSON.parse(sessionStorage.getItem(DRAFT)) || {};
  } catch {
    return {};
  }
};

export const VideoCreator = () => {
  const { user, login } = useAuth();
  const { ent, refresh } = useEntitlement();
  const draft = readDraft();
  const [idea, setIdea] = useState(draft.idea || "");
  const [voce, setVoce] = useState(draft.voce || "giulia");
  const [formato, setFormato] = useState(draft.formato || "tiktok");
  const [musica, setMusica] = useState(draft.musica || "energica");
  const [job, setJob] = useState(null);
  const [sending, setSending] = useState(false);
  const [paywall, setPaywall] = useState(false);
  const outRef = useRef(null);
  const working = sending || (job && job.stato !== "pronto" && job.stato !== "errore");

  useEffect(() => {
    sessionStorage.setItem(DRAFT, JSON.stringify({ idea, voce, formato, musica }));
  }, [idea, voce, formato, musica]);

  useEffect(() => {
    const saved = localStorage.getItem(JOB);
    if (user && saved) setJob({ id: saved, stato: "in_corso", fase: "script", progresso: 3 });
  }, [user]);

  useEffect(() => {
    if (!job?.id || job.stato === "pronto" || job.stato === "errore") return undefined;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/videos/${job.id}`);
        setJob(data);
        if (data?.stato === "pronto" || data?.stato === "errore") localStorage.removeItem(JOB);
        if (data?.stato === "errore") {
          toast.error(data.errore);
          refresh();
        }
        if (data?.stato === "pronto") refresh();
      } catch (e) {
        const code = e?.response?.status;
        if (code === 404 || code === 401) {
          localStorage.removeItem(JOB);
          setJob(null);
        }
      }
    }, 2500);
    return () => clearInterval(t);
  }, [job?.id, job?.stato, refresh]);

  useEffect(() => {
    if (job?.stato !== "pronto" || !ent || ent.abbonato) return undefined;
    const t = setTimeout(() => setPaywall(true), 4000);
    return () => clearTimeout(t);
  }, [job?.stato, ent]);

  const submit = async (e) => {
    e.preventDefault();
    if (idea.trim().length < 5) return toast.error("Scrivi la tua idea (almeno qualche parola).");
    if (!user) {
      toast("Accedi con Google per creare il tuo video");
      return login("/tool/mimis-video");
    }
    if (ent && !ent.puo_creare) return setPaywall(true);
    setSending(true);
    try {
      const { data } = await api.post("/videos", { idea: idea.trim(), voce, formato, musica });
      localStorage.setItem(JOB, data.id);
      setJob({ id: data.id, stato: "in_coda", fase: "script", progresso: 3 });
      outRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    } catch (err) {
      if (err?.response?.status === 402) setPaywall(true);
      else toast.error(errMsg(err));
    } finally {
      setSending(false);
    }
  };

  const reset = () => {
    setJob(null);
    setIdea("");
  };

  return (
    <div className="grid items-start gap-10 lg:grid-cols-12 lg:gap-14">
      <form onSubmit={submit} className="card-soft p-6 hover:translate-y-0 sm:p-8 lg:col-span-7" data-testid="video-creator-form">
        <label htmlFor="idea" className="font-display text-lg font-bold">Di cosa vuoi parlare?</label>
        <textarea id="idea" value={idea} onChange={(e) => setIdea(e.target.value.slice(0, 300))} rows={3} disabled={working}
          placeholder="Es: 3 trucchi per dormire meglio" data-testid="video-idea-input"
          className="mt-3 w-full resize-none rounded-2xl border border-[#e7e7ea] bg-[#F5F5F7] p-4 text-base outline-none transition-[border-color,background-color] focus:border-[#0066FF] focus:bg-white" />
        <p className="mt-1.5 text-right text-xs text-[#71717a]">{idea.length}/300</p>

        <p className="font-display mt-5 text-lg font-bold">Seleziona voce</p>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          {VOICES.map(({ id, label, desc, icon: Icon }) => (
            <Choice key={id} active={voce === id} onClick={() => !working && setVoce(id)} testId={`voice-option-${id}`}>
              <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl ${voce === id ? "bg-[#0066FF] text-white" : "bg-[#F5F5F7] text-[#111111]"}`}><Icon className="h-5 w-5" /></span>
              <span><span className="block font-semibold">{label}</span><span className="block text-sm text-[#52525b]">{desc}</span></span>
            </Choice>
          ))}
        </div>

        <p className="font-display mt-6 text-lg font-bold">Stile video</p>
        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
          {FORMATS.map(({ id, label, icon: Icon }) => (
            <Choice key={id} active={formato === id} onClick={() => !working && setFormato(id)} testId={`format-option-${id}`}>
              <Icon className={`h-5 w-5 shrink-0 ${formato === id ? "text-[#0066FF]" : "text-[#52525b]"}`} />
              <span className="text-sm font-semibold">{label}</span>
            </Choice>
          ))}
        </div>

        <p className="font-display mt-6 text-lg font-bold">Musica di sottofondo</p>
        <MusicPicker value={musica} onChange={setMusica} disabled={working} />

        <section className="mt-6" data-testid="tool-photos-section">
          <p className="font-display text-lg font-bold">Carica le tue 3 foto</p>
          <p className="mb-3 mt-1 text-sm text-[#52525b]">Facoltativo: le tue foto prendono il posto di 3 immagini create dall'AI nel video.</p>
          <BrandPhotos tid="tool" />
        </section>

        <button type="submit" disabled={working} className="btn-blu mt-8 min-h-[4.25rem] w-full px-6 text-base font-bold tracking-wide sm:text-lg" data-testid="create-video-button">
          {working ? <><Loader2 className="h-5 w-5 animate-spin" /> CREAZIONE IN CORSO...</> : <><Sparkles className="h-5 w-5" /> CREA IL MIO VIDEO ORA - 60 SECONDI</>}
        </button>
        <p className="mt-3 text-center text-sm text-[#52525b]" data-testid="credits-hint">
          {creditHint(user, ent)}
        </p>
      </form>

      <div ref={outRef} className="lg:col-span-5">
        <AnimatePresence initial={false}>
          {job?.stato === "pronto" ? (
            <motion.div key="result" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <VideoResult video={job} onNew={reset} trial={ent && !ent.abbonato} onUnlock={() => setPaywall(true)} />
            </motion.div>
          ) : job && job.stato !== "errore" ? (
            <motion.div key="progress" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="card-soft p-6 hover:translate-y-0 sm:p-8">
              <CreationProgress job={job} />
            </motion.div>
          ) : (
            <motion.div key="demo" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="relative mx-auto w-[250px] sm:w-[280px]">
              <div className="pointer-events-none absolute left-1/2 top-1/2 h-[115%] w-[170%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(circle,rgba(0,102,255,0.18),transparent_62%)]" />
              <PhoneDemo playing />
              <p className="mt-4 text-center text-sm text-[#52525b]">Esempio di video creato con MIMIS</p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <PaywallDialog open={paywall} onOpenChange={setPaywall} limit={!!ent?.abbonato} />
    </div>
  );
};
